import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import type { OpenOrderRow } from '../api/types'
import { StateCell } from '../components/StateCell'
import { NotesCell } from '../components/NotesCell'

/**
 * TanStack Table column definitions for the Open Orders DataView.
 * Order + content matches spec §8.1 "Keep" list; the dropped columns
 * (New DD / SPI Notes / PO Update / PO Ack'd / Allocated?) are
 * intentionally absent. `notes` was restored (2026-09-08, `notes_internal`
 * only) as a directly-editable column — see `NotesCell`.
 */
export function useOpenOrdersColumns(): ColumnDef<OpenOrderRow, unknown>[] {
  return useMemo<ColumnDef<OpenOrderRow, unknown>[]>(() => {
    return [
      {
        id: 'customer',
        header: 'Customer',
        accessorKey: 'customer',
        cell: (ctx) => <span className="text-primary">{ctx.getValue() as string}</span>,
      },
      { id: 'po_number', header: 'P.O. #', accessorKey: 'po_number' },
      { id: 'po_ln', header: 'PO:LN', accessorKey: 'po_ln' },
      {
        id: 'so_number',
        header: 'SO #',
        accessorKey: 'so_number',
        cell: (ctx) => (
          <span className="text-link">{ctx.getValue() as string}</span>
        ),
      },
      { id: 'line', header: 'Line', accessorKey: 'line', meta: { numeric: true } },
      {
        id: 'customer_part_number',
        header: 'Cust.Part#',
        accessorKey: 'customer_part_number',
      },
      { id: 'product_name', header: 'Product', accessorKey: 'product_name' },
      { id: 'due_on', header: 'Due On', accessorKey: 'due_on' },
      {
        id: 'confirmation_date',
        header: 'Order Confirmed',
        accessorKey: 'confirmation_date',
      },
      {
        id: 'last_revision',
        header: 'Last Revision',
        accessorKey: 'last_revision',
      },
      { id: 'ordered', header: 'Ordered', accessorKey: 'ordered', meta: { numeric: true } },
      { id: 'open', header: 'Open', accessorKey: 'open', meta: { numeric: true } },
      {
        id: 'on_hand',
        header: 'On Hand',
        accessorKey: 'on_hand',
        meta: { numeric: true },
      },
      {
        id: 'replenish_on_order',
        header: 'Replenish',
        accessorKey: 'replenish_on_order',
        cell: (ctx) => (ctx.getValue() ? 'Yes' : 'No'),
      },
      {
        id: 'state',
        header: 'State',
        cell: (ctx) => <StateCell row={ctx.row.original} />,
      },
      {
        id: 'odoo_last_updated',
        header: 'Last Updated',
        accessorKey: 'odoo_last_updated',
      },
      { id: 'vendor_po', header: 'Vendor PO', accessorKey: 'vendor_po' },
      { id: 'vendor', header: 'Vendor', accessorKey: 'vendor' },
      { id: 'po_due', header: 'PO Due', accessorKey: 'po_due' },
      {
        id: 'notes',
        header: 'Notes',
        cell: (ctx) => <NotesCell row={ctx.row.original} />,
      },
    ]
  }, [])
}
